import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOutlined } from '@ant-design/icons';
import LoginModal from '../components/LoginModal';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

const Home = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videosLoaded, setVideosLoaded] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [showFooter, setShowFooter] = useState(false);
  const videoRefs = useRef([]);
  const canvasRef = useRef(null);
  const footerRef = useRef(null);
  const navigate = useNavigate();
  const { authenticated, logout } = useAuth();

  // 视频列表
  const videoList = [
    { url: '/web-new-1080p-1000kbps-001.mp4', loaded: false },
    { url: '/web-new-1080p-1000kbps-002.mp4', loaded: false },
    { url: '/web-new-1080p-1000kbps-003.mp4', loaded: false }
  ];

  // 背景图片
  const bgImage = '/bg-img.jpg';

  // 更新时间
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 格式化时间
  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return { hours, minutes, seconds };
  };

  const time = formatTime(currentTime);

  // 视频加载完成
  const onVideoLoaded = useCallback((index) => {
    setLoadedCount(prev => {
      const newCount = prev + 1;
      if (newCount === videoList.length) {
        setVideosLoaded(true);
        if (videoRefs.current[0]) {
          videoRefs.current[0].play();
        }
      }
      return newCount;
    });
  }, [videoList.length]);

  // 视频播放结束，切换到下一个
  const onVideoEnd = useCallback(() => {
    const nextIndex = (currentVideoIndex + 1) % videoList.length;
    switchVideo(nextIndex);
  }, [currentVideoIndex, videoList.length]);

  // 切换视频
  const switchVideo = useCallback((index) => {
    if (index === currentVideoIndex) return;
    
    const currentVideo = videoRefs.current[currentVideoIndex];
    const nextVideo = videoRefs.current[index];
    
    if (currentVideo) {
      currentVideo.pause();
    }
    
    setCurrentVideoIndex(index);
    
    if (nextVideo) {
      nextVideo.currentTime = 0;
      nextVideo.play();
    }
  }, [currentVideoIndex]);

  // Canvas 鼠标轨迹效果
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let trails = [];
    let mouse = { x: 0, y: 0, lastX: 0, lastY: 0, speed: 0 };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    class Trail {
      constructor(x, y, speed, angle) {
        this.x = x;
        this.y = y;
        this.size = Math.min(speed * 1.5, 12);
        this.angle = angle;
        this.opacity = Math.min(speed * 0.08, 0.6);
        this.life = 1;
        this.decay = 0.02;
        this.type = Math.random() > 0.5 ? 'circle' : 'line';
      }

      update() {
        this.life -= this.decay;
        this.x += Math.cos(this.angle) * 0.5;
        this.y += Math.sin(this.angle) * 0.5;
        this.size *= 0.98;
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.life * this.opacity;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
        ctx.lineWidth = 1;

        if (this.type === 'circle') {
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(this.x - this.size * 0.3, this.y - this.size * 0.3, this.size * 0.3, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.moveTo(this.x, this.y);
          ctx.lineTo(
            this.x - Math.cos(this.angle) * this.size * 3,
            this.y - Math.sin(this.angle) * this.size * 3
          );
          ctx.stroke();
        }

        ctx.restore();
      }
    }

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      const dx = mouse.x - mouse.lastX;
      const dy = mouse.y - mouse.lastY;
      mouse.speed = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);

      if (mouse.speed > 2) {
        const count = Math.min(Math.floor(mouse.speed / 5), 3);
        for (let i = 0; i < count; i++) {
          const offsetX = (Math.random() - 0.5) * 10;
          const offsetY = (Math.random() - 0.5) * 10;
          trails.push(new Trail(
            mouse.x + offsetX,
            mouse.y + offsetY,
            mouse.speed,
            angle + (Math.random() - 0.5) * 0.2
          ));
        }
      }

      mouse.lastX = mouse.x;
      mouse.lastY = mouse.y;
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      trails = trails.filter(trail => {
        trail.update();
        trail.draw();
        return trail.life > 0;
      });
      if (trails.length > 50) {
        trails = trails.slice(-50);
      }
      animationId = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMouseMove);
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, []);

  // 滚动显示 footer
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowFooter(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Footer 双击：未登录时打开登录弹窗，已登录时退出登录
  const handleFooterDoubleClick = () => {
    if (authenticated) {
      logout();
    } else {
      setLoginModalVisible(true);
    }
  };

  return (
    <div className="home-container">
      {/* 视频背景层 */}
      <div className="video-container">
        <div 
          className={`video-placeholder ${videosLoaded ? 'hidden' : ''}`}
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        
        {videoList.map((video, index) => (
          <div
            key={index}
            className={`video-wrapper ${currentVideoIndex === index ? 'active' : ''}`}
          >
            <video
              ref={el => videoRefs.current[index] = el}
              src={video.url}
              muted
              playsInline
              onEnded={onVideoEnd}
              onLoadedData={() => onVideoLoaded(index)}
            />
          </div>
        ))}
      </div>

      {/* Canvas 交互层 */}
      <canvas ref={canvasRef} className="canvas-overlay" />

      {/* 渐变遮罩 */}
      <div className="gradient-overlay" />

      {/* 左上角备案名 */}
      <div className="record-name">个人生活的记录</div>

      {/* 右上角博客图标 - 登录后才显示 */}
      {authenticated && (
        <div className="gallery-icon" onClick={() => navigate('/blogs')} title="博客列表">
          <BookOutlined style={{ fontSize: '32px', color: '#ffffff', textShadow: '0 0 10px rgba(0,0,0,0.8), 0 0 20px rgba(0,0,0,0.6), 0 2px 4px rgba(0,0,0,0.9)' }} />
        </div>
      )}

      {/* 主内容 */}
      <div className="content">
        <div className="brand-container">
          <div className="brand-name-cn">制服狐狸</div>
          <div className="brand-name-en">UniForm Fox</div>
          <p className="brand-description">
            迷恋于严谨剪裁与秩序之美，<br/>
            在规整的线条间寻找某种触感的仪式。<br/>
            一种关于克制与纯粹的私人收藏。
          </p>
        </div>
      </div>

      {/* 左下角时间 */}
      <div className="time-display">
        {time.hours}<span>:</span>{time.minutes}<span>:</span>{time.seconds}
      </div>

      {/* 底部备案信息 */}
      <footer ref={footerRef} className={`footer-section ${showFooter ? 'visible' : ''}`}>
        <div className="footer-box">
          <div className="footer-item">© Uniform Fox</div>
          <div className="footer-item">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">陕ICP备18010394号-3</a>
          </div>
          <div className="footer-item">
            <img
              src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAMAAAC6V+0/AAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAACZUExURUxpceS2afDfqde+ke/fsc67duW4ZgEBcfbpv3NyeuOxYunKg+vgv923YujLiOW7cn94ePDQbOnGbdvGn56LfOO1WvbUa+zAWOq5VNiXR+CaQcgKDORtMPPIWtcnFtM9IeSEN0AXRdVKJ+KVO4x1XvnQX9IZEnBnaOV7NK2IVN9gK8q/g76OTxYUaNlWKk8VPbwMEIBtX3ETL+zEVd8AAAAVdFJOUwC+KYdB/t3+Gf3OqlL9lrbw9+lryeYe1b8AAAEYSURBVBjTTdFHcsMwDAVQyypQteRCsIvq1TX3P1zoKA0LLN4AM5iP3W6rML1czuHuf3lOd391c+ekfxZe65daVObX19/hMK+7SZq7FH6dfOue1GNVlmUzSB3Vp/2GHcyLLAdp5LJC8YWhU8Bs5GBMKc0KUfJWV/hwreS9ZXxQPhT92eKhzWBkjVGT4jjCKg4Wgwpv0FSNWYxScMM2tkgmFsGzVI2qSgmRrgKLuejX2+M52JuaD/CRvNedlmABAI/atgiJyC0eOW/R76KZjGNEhSBoMWWcK429Zj32Eyfae1+fHlnVIiUUkfPjydtyC6gVSjW1q1m2YYJIiGUmOCGIyZbcgTDNCBNUM31yfxL1XCeOgyDO3e0jnyf3HELhGUqdAAAAAElFTkSuQmCC"
              className="ghs"
              alt="公安备案"
            />
            <a href="http://www.beian.gov.cn/portal/index.do" target="_blank" rel="noopener noreferrer">陕公网安备 61011302000879号</a>
          </div>
        </div>
        {/* 透明双击区域 */}
        <div className="login-trigger" onDoubleClick={handleFooterDoubleClick} title="双击登录" />
      </footer>

      {/* 登录弹窗 */}
      <LoginModal 
        visible={loginModalVisible}
        onClose={() => setLoginModalVisible(false)}
      />
    </div>
  );
};

export default Home;
